/**
 * A2 — Historial de importaciones de clientes, con «Deshacer».
 *
 * Deshacer borra definitivamente lo que creó el lote (clientes y contactos) y solo se
 * permite mientras esos clientes no tengan expedientes, documentos, facturas… La API lo
 * comprueba y, si no se puede, explica por qué.
 */
import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, Loader2, Undo2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { count } from '../../lib/plural'
import type { ClientImportBatchDto, ClientImportBatchListResponse } from '@toga/shared'

interface Props {
  /** Cambia cuando se acaba de importar algo, para recargar la lista. */
  refreshKey: number
  /** Avisa de que se deshizo un lote (la lista de clientes cambió). */
  onUndone?: () => void
}

export default function ImportHistory({ refreshKey, onUndone }: Props) {
  const [items, setItems] = useState<ClientImportBatchDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const r = await api.get<ClientImportBatchListResponse>('/clients/import/batches')
      setItems(r.items)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el historial')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  const undo = async (b: ClientImportBatchDto) => {
    const ok = confirm(
      `¿Deshacer la importación de «${b.fileName}»?\n\n` +
        `Se borrarán definitivamente ${count(b.clientsCreated, 'cliente', 'clientes')} y ${count(b.contactsCreated, 'contacto', 'contactos')} ` +
        'que creó, incluidos los cambios que les hayas hecho después.' +
        (b.clientsFilled > 0
          ? `\n\n${count(b.clientsFilled, 'cliente que solo se completó conserva', 'clientes que solo se completaron conservan')} esos datos.`
          : '')
    )
    if (!ok) return
    setBusyId(b.id)
    setError(null)
    try {
      await api.post(`/clients/import/batches/${b.id}/undo`)
      await load()
      onUndone?.()
    } catch (err) {
      const body =
        err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'No se pudo deshacer'))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <section className="surface p-5 space-y-3">
      <h3 className="text-sm font-semibold t-primary">Historial de importaciones</h3>

      {error && (
        <div className="text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {items === null ? (
        <div className="t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <p className="t-muted text-sm">Todavía no se ha importado nada.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider t-faint border-b border-base">
                <th className="py-2 pr-3 font-medium">Fecha</th>
                <th className="py-2 pr-3 font-medium">Fichero</th>
                <th className="py-2 pr-3 font-medium">Resultado</th>
                <th className="py-2 pr-3 font-medium">Origen declarado</th>
                <th className="py-2 font-medium text-right">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} className="border-b border-base/50 last:border-b-0 align-top">
                  <td className="py-2.5 pr-3 t-secondary text-xs whitespace-nowrap">
                    {new Date(b.createdAt).toLocaleString('es-ES', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                    <div className="t-faint">{b.createdByName ?? '—'}</div>
                  </td>
                  <td className="py-2.5 pr-3 t-primary text-xs break-all">{b.fileName}</td>
                  <td className="py-2.5 pr-3 t-secondary text-xs">
                    {count(b.clientsCreated, 'nuevo', 'nuevos')}
                    {b.clientsFilled > 0 && (
                      <>, {count(b.clientsFilled, 'completado', 'completados')}</>
                    )}
                    <div className="t-faint">
                      {count(b.contactsCreated, 'contacto', 'contactos')} · {b.clientsSkipped} sin
                      importar
                    </div>
                  </td>
                  <td className="py-2.5 pr-3 t-muted text-xs max-w-[28ch] break-words">
                    {b.origin}
                  </td>
                  <td className="py-2.5 text-right">
                    {b.undoneAt ? (
                      <span className="text-xs t-faint">
                        Deshecha el {new Date(b.undoneAt).toLocaleDateString('es-ES')}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void undo(b)}
                        disabled={busyId === b.id}
                        className="px-2.5 py-1.5 rounded-lg text-xs border border-base t-secondary hover:bg-rose-500/10 hover:text-rose-300 inline-flex items-center gap-1.5 disabled:opacity-50"
                      >
                        {busyId === b.id ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Undo2 size={12} />
                        )}
                        Deshacer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
